"use client";

/**
 * Shared state for the residence follow hook. PageClient owns ONE
 * instance and hands it to both surfaces (the hero FollowResidenceButton
 * and the intent-timed FollowNudge), so status is fetched once and the
 * two can never disagree about whether this device follows the complex.
 *
 * Identity model: the browser's push endpoint IS the identity for
 * anonymous visitors (unguessable per-device URL); a session, when one
 * exists, additionally binds the follow to the account server-side.
 * A browser that cannot push (iOS Safari tab, refused permission) can
 * follow through the Telegram bot instead: the follow then lives in
 * that chat, and this device only remembers that it sent the person
 * there (a local mark), since the chat has no identity we can see.
 */
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";
import { trackEvent } from "@/lib/tracking";
import { telegramFollowHref } from "@/lib/telegram";
import {
  getCurrentPushEndpoint,
  getOrCreatePushSubscription,
  isPushEligible,
} from "@/lib/push";

export type FollowState = "unknown" | "idle" | "following" | "busy";

export type FollowFailReason =
  | "account_required"
  | "denied"
  | "unsupported"
  | "disabled"
  | "error";

export type FollowResult = { ok: true } | { ok: false; reason: FollowFailReason };

export interface ResidenceFollowHook {
  /** Mark the button as following after the alert-based path succeeded. */
  markFollowing: () => void;
  /** Which channel carries the follow: this browser's push, a saved
   *  search on the account (Telegram / e-mail), or the Telegram bot
   *  opened from this device. Null while not following. */
  via: "push" | "alert" | "telegram" | null;
  /** Open the bot's follow deep link (new tab) and remember it here. */
  followViaTelegram: () => void;
  /** Drop this device's memory of a Telegram follow (the chat keeps it
   *  until the person taps "stop following" in the bot). */
  forgetTelegram: () => void;
  /** Current device's relationship to this residence. */
  state: FollowState;
  /** Web push is possible in this browser (false on iOS Safari tabs). */
  eligible: boolean;
  follow: () => Promise<FollowResult>;
  unfollow: () => Promise<void>;
}

/** Shared account_required handler: login with a return path back to
 *  the residence page the visitor was on. With ``follow`` the page
 *  finishes the follow itself once the session exists (?follow=1). */
export function goToResidenceLogin(clusterId: string, opts: { follow?: boolean } = {}) {
  const m = window.location.pathname.match(/^\/(az|ru|en)\b/);
  const loc = m ? m[1] : "az";
  const returnTo = encodeURIComponent(
    `/${loc}/residence/${clusterId}${opts.follow ? "?follow=1" : ""}`,
  );
  window.location.href = `/${loc}/auth/login?return_to=${returnTo}`;
}

export type AlertFollowResult =
  | { ok: true }
  | { ok: false; reason: "not_authenticated" | "email_not_verified" | "limit" | "error" };

/** Push is blocked or unsupported and the visitor is signed in: follow
 *  through a saved search scoped to the residence instead. Same promise
 *  (new listings and price moves here), delivered by Telegram or e-mail
 *  through the alert matcher. Every listing counts (tier "any"); the
 *  free daily digest cadence. A duplicate means it already exists,
 *  which is a success. */
export async function followViaAlert(clusterId: string): Promise<AlertFollowResult> {
  try {
    const r = await fetch(`${API_BASE}/alerts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        filters: { complex_slugs: [clusterId], geo_mode: "area" },
        min_verdict_tier: "any",
        channel: "telegram",
        notify_frequency: "24h",
      }),
    });
    if (r.ok || r.status === 409) {
      trackEvent("follow.residence.done", { cluster_id: clusterId, via: "alert" });
      return { ok: true };
    }
    if (r.status === 401) return { ok: false, reason: "not_authenticated" };
    const detail = (await r.json().catch(() => ({})))?.detail;
    if (r.status === 403 || (detail && typeof detail === "object" && detail.code === "email_not_verified")) {
      return { ok: false, reason: "email_not_verified" };
    }
    if (detail && typeof detail === "object" && detail.reason === "limit_reached") {
      return { ok: false, reason: "limit" };
    }
    return { ok: false, reason: "error" };
  } catch {
    return { ok: false, reason: "error" };
  }
}

const TG_MARK_PREFIX = "resfollow:tg:";

function readTelegramMark(clusterId: string): boolean {
  try {
    return window.localStorage.getItem(TG_MARK_PREFIX + clusterId) !== null;
  } catch {
    return false;
  }
}

export function useResidenceFollow(
  clusterId: string,
  locale: string,
): ResidenceFollowHook {
  const [state, setState] = useState<FollowState>("unknown");
  const [via, setVia] = useState<"push" | "alert" | "telegram" | null>(null);
  // true during SSR/hydration, resolved in the effect - keeps server and
  // first client render identical. It started false, so every page was
  // drawn with the Telegram button and its two-line hint, which most
  // browsers (desktop and Android Chrome take push) then removed: the
  // hero and the gallery under it jumped (Google's run, 2026-10-27).
  // Browsers without push (iPhone Safari) add the hint after load.
  const [eligible, setEligible] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const ok = isPushEligible();
    setEligible(ok);
    // Sent to the bot from this device earlier: that is the follow this
    // device knows about, whatever push can or cannot do now.
    if (readTelegramMark(clusterId)) {
      setState("following");
      setVia("telegram");
      return;
    }
    if (!ok) {
      setState("idle");
      return;
    }
    (async () => {
      // No local subscription -> can't be following from this device
      // (status by session-only is possible, but the button's promise
      // is "notify THIS device", so the endpoint is the truth).
      const endpoint = await getCurrentPushEndpoint();
      if (!endpoint) {
        if (!cancelled) setState("idle");
        return;
      }
      try {
        const r = await fetch(`${API_BASE}/follows/residence/status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ cluster_id: clusterId, endpoint }),
        });
        const d = (await r.json()) as { following?: boolean; via?: "push" | "alert" | null };
        if (!cancelled) {
          setState(d?.following ? "following" : "idle");
          setVia(d?.following ? (d.via ?? "push") : null);
        }
      } catch {
        if (!cancelled) setState("idle");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clusterId]);

  const follow = useCallback(async (): Promise<FollowResult> => {
    setState("busy");
    const { sub, reason } = await getOrCreatePushSubscription("/follows/config");
    if (!sub) {
      setState("idle");
      return {
        ok: false,
        reason: (reason ?? "error") as FollowFailReason,
      };
    }
    try {
      const r = await fetch(`${API_BASE}/follows/residence`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          cluster_id: clusterId,
          locale,
          subscription: sub.toJSON(),
        }),
      });
      if (r.status === 403) {
        setState("idle");
        return { ok: false, reason: "account_required" };
      }
      if (!r.ok) {
        setState("idle");
        return { ok: false, reason: "error" };
      }
      setState("following");
      setVia("push");
      trackEvent("follow.residence.done", { cluster_id: clusterId });
      return { ok: true };
    } catch {
      setState("idle");
      return { ok: false, reason: "error" };
    }
  }, [clusterId, locale]);

  const unfollow = useCallback(async () => {
    const endpoint = await getCurrentPushEndpoint();
    setState("busy");
    try {
      await fetch(`${API_BASE}/follows/residence/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cluster_id: clusterId, endpoint }),
      });
    } catch {
      /* device-side state below is the honest fallback either way */
    } finally {
      setState("idle");
      setVia(null);
    }
  }, [clusterId]);

  const markFollowing = useCallback(() => {
    setState("following");
    setVia("alert");
  }, []);
  const followViaTelegram = useCallback(() => {
    // A new tab, so the page stays where the visitor was; the bot
    // confirms in the chat. The mark is the only thing this device
    // keeps, and only after the tap (the deep link cannot be known to
    // have been completed).
    window.open(telegramFollowHref(clusterId), "_blank", "noopener");
    try {
      window.localStorage.setItem(TG_MARK_PREFIX + clusterId, String(Date.now()));
    } catch {
      /* private mode: the chat still holds the follow */
    }
    setState("following");
    setVia("telegram");
    trackEvent("follow.residence.done", { cluster_id: clusterId, via: "telegram" });
  }, [clusterId]);
  const forgetTelegram = useCallback(() => {
    try {
      window.localStorage.removeItem(TG_MARK_PREFIX + clusterId);
    } catch {
      /* nothing to forget */
    }
    setState("idle");
    setVia(null);
  }, [clusterId]);
  return { state, eligible, follow, unfollow, markFollowing, via, followViaTelegram, forgetTelegram };
}
